import React, { useState, useMemo } from 'react';
import {
  Datagrid,
  EditButton,
  Filter,
  List,
  TextField,
  TextInput,
  SimpleForm,
  Create,
  ArrayInput,
  SimpleFormIterator,
  SingleFieldList,
  ArrayField,
  ChipField,
  SelectInput,
  FunctionField,
  SelectField,
  AutocompleteArrayInput,
  required,
  BooleanInput,
  BooleanField,
  useNotify,
  useRedirect,
  useCreate,
  useUpdate,
  useGetOne,
  Toolbar,
  useDataProvider
} from 'react-admin';
import { equals } from 'ramda';
import { useFormState } from 'react-final-form';
import { Button } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import SpecificFieldsEdit from '../../components/SpecificFieldsEdit';
import useInteractiionTypeGroupsForAutoComplete from '../../utils/hooks/useInteractionTypeGroupsForAutoComplete';
import useInteractionTypesForAutoComplete from '../../utils/hooks/useInteractionTypesForAutoComplete';

const OnlySaveToolbar = ({ isEdit = false, groups, type, viewId }) => {
  const { values, valid, dirty } = useFormState();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const [create] = useCreate();
  const [update] = useUpdate();
  const [saving, setSaving] = useState(false);

  const { data: previousData } = useGetOne('InteractionTypeView', viewId, {
    enabled: isEdit && !!viewId
  });

  const handleSave = async () => {
    setSaving(true);

    try {
      const { typeGroup, ...interactionTypeData } = values;
      const originalId = isEdit ? values.originalId : values.id;
      const currentGroupIds = typeGroup || [];

      if (isEdit) {
        if (!equals(interactionTypeData, previousData)) {
          await update(
            'InteractionType',
            type.nodeId, // use the nodeId from the interaction type table, not view
            {
              ...interactionTypeData,
              nodeId: type.nodeId
            },
            {
              ...previousData,
              nodeId: type.nodeId
            },
            { returnPromise: true }
          );
        }
      } else {
        await create('InteractionType', interactionTypeData, {
          returnPromise: true
        });
      }

      const previousGroupIds = groups
        .filter((group) => (group.types || []).includes(originalId))
        .map((group) => group.nodeId);

      const toAdd = currentGroupIds.filter(
        (groupId) => !previousGroupIds.includes(groupId)
      );
      const toRemove = previousGroupIds.filter(
        (groupId) => !currentGroupIds.includes(groupId)
      );

      await Promise.all(
        toAdd.map(async (groupId) => {
          const { data: group } = await dataProvider.getOne(
            'InteractionTypeGroup',
            {
              id: groupId
            }
          );

          const updatedTypes = Array.from(
            new Set([...(group.types || []), originalId])
          );

          await update(
            'InteractionTypeGroup',
            groupId,
            { ...group, types: updatedTypes },
            group || {},
            { returnPromise: true }
          );
        })
      );

      await Promise.all(
        toRemove.map(async (groupId) => {
          const { data: group } = await dataProvider.getOne(
            'InteractionTypeGroup',
            {
              id: groupId
            }
          );

          const updatedTypes = (group.types || []).filter(
            (t) => t !== originalId
          );

          await update(
            'InteractionTypeGroup',
            groupId,
            { ...group, types: updatedTypes },
            group || {},
            { returnPromise: true }
          );
        })
      );

      notify(isEdit ? 'עודכן בהצלחה' : 'נוצר בהצלחה', { type: 'info' });
      redirect('/InteractionTypeView');
    } catch (e) {
      notify(e?.message || 'שגיאה', { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Toolbar>
      <Button
        startIcon={<SaveIcon />}
        color="primary"
        variant="contained"
        onClick={handleSave}
        disabled={!valid || !dirty || saving}
      >
        שמור
      </Button>
    </Toolbar>
  );
};

// Converts string tags to ChipField with label
export const StringToLabelObject = ({ record, children, ...rest }) =>
  React.cloneElement(children, {
    record: { label: record },
    ...rest
  });

export const InteractionTypesList = (props) => {
  const { groups } = useInteractiionTypeGroupsForAutoComplete(); // ✅ Use the existing hook

  return (
    <List
      bulkActionButtons={false}
      {...props}
      filters={<InteractionTypesFilter />}
      sort={{ field: 'interactionTypeId', order: 'ASC' }}
      perPage={25}
      filter={{
        specificFields: [
          'nodeId',
          'id',
          'label',
          'status',
          'tags',
          'active',
          'originalId',
          'mokedActive',
          'carwizAgentActive',
          'interactionTypeId',
          'groupIds'
        ]
      }}
    >
      <Datagrid>
        <TextField source="originalId" label="מזהה" />
        <TextField source="label" label="שם סטטוס" />
        <BooleanField source="active" label="סינון בניהול וטיפול בלקוחות" />
        <BooleanField source="carwizAgentActive" label="הוספת סטטוס ע״י נציג" />
        <BooleanField source="mokedActive" label="סינון במסך ניהול מוקד" />

        <ArrayField source="tags" label="תגיות">
          <SingleFieldList>
            <StringToLabelObject>
              <ChipField source="label" />
            </StringToLabelObject>
          </SingleFieldList>
        </ArrayField>

        <FunctionField
          label="סל"
          render={(record) => {
            const typeId = record.originalId;
            if (!typeId || !groups?.length) return null;

            const groupNames = groups
              .filter((g) => g.types?.includes(typeId))
              .map((g) => g.name);

            const displayText =
              groupNames.length > 5
                ? `${groupNames.slice(0, 5).join(', ')}...`
                : groupNames.join(', ');

            return (
              <div
                style={{
                  maxWidth: 300,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                title={groupNames.join(', ')} // Tooltip on hover
              >
                {displayText}
              </div>
            );
          }}
        />
        <SelectField
          source="status"
          choices={[
            { id: 'WAITING', name: 'ממתינים לטיפול' },
            { id: 'DONE', name: 'טופלו' }
          ]}
          label="עמודה"
        />

        <EditButton />
      </Datagrid>
    </List>
  );
};

const InteractionTypesFilter = ({ ...props }) => {
  // const { groups } = useInteractiionTypeGroupsForAutoComplete();

  return (
    <Filter {...props}>
      <TextInput
        source="id"
        label="מזהה"
        variant="filled"
        margin="dense"
        parse={(value) => (value ? { filter: 'includes', value } : null)}
        format={(value) =>
          typeof value === 'object' &&
          value !== null &&
          'filter' in value &&
          'value' in value
            ? value.value
            : value
        }
      />
      <TextInput source="label" label="שם סטטוס" />

      {/* Custom boolean filters with toggle behavior */}
      <BooleanInput
        source="active"
        label="הצגה בניהול וטיפול"
        parse={(value) => (value === true ? true : undefined)}
      />
      <BooleanInput
        source="mokedActive"
        label="הצגה בניהול מוקד"
        parse={(value) => (value === true ? true : undefined)}
      />
      <BooleanInput
        source="carwizAgentActive"
        label="הוספת סטטוס ע״י נציג"
        parse={(value) => (value === true ? true : undefined)}
      />
      {/* <AutocompleteArrayInput
        label="סינון לפי קבוצה"
        source="groups_text" // Must match your backend field
        choices={groups}
        optionValue="nodeId"
        optionText="name"
        parse={(selectedNodeIds) => {
          if (!selectedNodeIds || !selectedNodeIds.length) return undefined;

          const selectedNames = selectedNodeIds
            .map((id) => groups.find((g) => g.nodeId === id)?.name)
            .filter(Boolean);

          return selectedNames.join(','); // 👉 Return string
        }}
        format={(value) => {
          if (typeof value !== 'string') return [];
          const names = value.split(',').map((s) => s.trim());
          return names
            .map((name) => groups.find((g) => g.name === name)?.nodeId)
            .filter(Boolean);
        }}
      /> */}

      {/* <TextInput
      source="tags"
      label="תגיות"
      parse={(value) => value || undefined}
      format={(value) => value || ''}
    /> */}
    </Filter>
  );
};

// Mutation form with injected custom toolbar and save
const InteractionTypesMutation = (props) => {
  const { groups } = useInteractiionTypeGroupsForAutoComplete();
  const { types } = useInteractionTypesForAutoComplete(
    props.record?.originalId || null
  );
  const isEdit = props.disableId;
  const record = props.record || {};

  // Calculate defaultGroupNodeIds only once all data is ready
  const defaultGroupNodeIds = useMemo(() => {
    if (!isEdit || !groups.length || !record.originalId) return [];

    return groups
      .filter((group) => group.types?.includes(record.originalId))
      .map((group) => group.nodeId);
  }, [isEdit, groups, record.originalId]);

  if (
    !groups ||
    groups.length === 0 ||
    types.length === 0 ||
    (isEdit && !record)
  )
    return null;

  return (
    <SimpleForm
      {...props}
      initialValues={{
        ...record,
        typeGroup: defaultGroupNodeIds
      }}
      key={`${groups.length}-${record.originalId}-${defaultGroupNodeIds.length}`}
      toolbar={
        <OnlySaveToolbar
          isEdit={isEdit}
          groups={groups}
          type={types[0] || null}
          viewId={record.id}
        />
      }
    >
      {isEdit ? (
        <TextInput disabled label="מזהה" source="originalId" />
      ) : (
        <TextInput label="מזהה" source="id" validate={required()} />
      )}
      <TextInput source="label" />

      <AutocompleteArrayInput
        label="סלי אינטרקציות"
        source="typeGroup"
        choices={groups}
        optionValue="nodeId"
        optionText="name"
        // defaultValue={defaultGroupNodeIds} // <-- Set default value here
      />

      <SelectInput
        source="status"
        choices={[
          { id: 'WAITING', name: 'ממתינים לטיפול' },
          { id: 'DONE', name: 'טופלו' }
        ]}
        label="עמודה"
      />
      <BooleanInput source="active" label="הצגה במסכים ניהול וטיפול" />
      <BooleanInput source="mokedActive" label="הצגה בניהול מוקד" />
      <BooleanInput source="carwizAgentActive" label="דיווח נציג" />
      <ArrayInput source="tags">
        <SimpleFormIterator>
          <TextInput />
        </SimpleFormIterator>
      </ArrayInput>
    </SimpleForm>
  );
};

export const InteractionTypesEdit = (props) => (
  <SpecificFieldsEdit
    {...props}
    disableId
    validate={required()}
    specificFields={[
      'nodeId',
      'id',
      'label',
      'status',
      'tags',
      'active',
      'mokedActive',
      'carwizAgentActive',
      'interactionTypeId',
      'groupIds'
    ]}
  >
    <InteractionTypesMutation {...props} disableId />
  </SpecificFieldsEdit>
);

export const InteractionTypesCreate = (props) => (
  <Create {...props}>
    <InteractionTypesMutation {...props} />
  </Create>
);
